import type { SyntheticEvent } from 'react';

import type { TFunction } from 'i18next';

import { isGermanNumberFormat, localizeDecimalSeparator } from './numberFormat';

export const localeAmount = ({
  amount,
  currency,
  countryCode,
  locale = 'en',
}: {
  amount: number;
  currency: string;
  countryCode: string;
  locale?: string;
}) => {
  const hasDecimals = amount % 1 !== 0;
  // German players: "19,99 €" (symbol after the number) whatever their country is.
  const intlLocale = isGermanNumberFormat() ? 'de-DE' : `${locale}-${countryCode.toUpperCase()}`;
  return amount.toLocaleString(intlLocale, {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: hasDecimals ? 2 : 0,
  });
};

export const floorToDecimals = (value: number, decimals: number) => {
  const factor = Math.pow(10, decimals);
  return Math.floor(value * factor) / factor;
};

type FormatValueWithKArgs = {
  amount: number;
  mode: 'round' | 'floor';
  decimals?: number;
  t: TFunction;
};

export const formatValueWithKRaw = ({ amount, mode, decimals, t }: FormatValueWithKArgs): string => {
  const thousandAbbr = t('numbers.thousand');
  const millionAbbr = t('numbers.million');
  const billionAbbr = t('numbers.billion');

  if (amount >= 1000000000) {
    const billions = amount / 1000000000;
    if (mode === 'round') {
      if (Number.isInteger(billions)) {
        return `${billions.toFixed(0)}${billionAbbr}`;
      } else if (Number.isInteger(billions * 10)) {
        return `${billions.toFixed(decimals ?? 1)}${billionAbbr}`;
      }
      return `${billions.toFixed(decimals ?? 2)}${billionAbbr}`;
    } else {
      if (Number.isInteger(billions)) {
        return `${Math.floor(billions).toFixed(decimals ?? 0)}${billionAbbr}`;
      } else if (Number.isInteger(billions * 10)) {
        const floored = floorToDecimals(billions, 1);
        return `${floored.toFixed(decimals ?? 1)}${billionAbbr}`;
      }
      const floored = floorToDecimals(billions, 2);
      return `${floored.toFixed(decimals ?? 2)}${billionAbbr}`;
    }
  }

  if (amount >= 1000000) {
    const millions = amount / 1000000;
    if (mode === 'round') {
      if (Number.isInteger(millions)) {
        return `${millions.toFixed(0)}${millionAbbr}`;
      } else if (Number.isInteger(millions * 10)) {
        return `${millions.toFixed(decimals ?? 1)}${millionAbbr}`;
      }
      return `${millions.toFixed(decimals ?? 2)}${millionAbbr}`;
    } else {
      if (Number.isInteger(millions)) {
        return `${Math.floor(millions).toFixed(decimals ?? 0)}${millionAbbr}`;
      } else if (Number.isInteger(millions * 10)) {
        const floored = floorToDecimals(millions, 1);
        return `${floored.toFixed(decimals ?? 1)}${millionAbbr}`;
      }
      const floored = floorToDecimals(millions, 2);
      return `${floored.toFixed(decimals ?? 2)}${millionAbbr}`;
    }
  }

  if (amount >= 1000) {
    const thousands = amount / 1000;
    if (mode === 'round') {
      const isInteger = Number.isInteger(thousands);
      return `${thousands.toFixed(isInteger ? 0 : (decimals ?? 1))}${thousandAbbr}`;
    }
    if (Number.isInteger(thousands)) {
      return `${Math.floor(thousands).toFixed(0)}${thousandAbbr}`;
    } else if (Number.isInteger(thousands * 10)) {
      const floored = floorToDecimals(thousands, 1);
      return `${floored.toFixed(decimals ?? 1)}${thousandAbbr}`;
    }
    const floored = floorToDecimals(thousands, 2);
    return `${floored.toFixed(decimals ?? 2)}${thousandAbbr}`;
  }

  if (mode === 'round') {
    const numStr = amount.toString();
    const decimalIndex = numStr.indexOf('.');
    if (decimalIndex === -1 || numStr.length - decimalIndex <= 3) {
      return numStr;
    }
    return amount.toFixed(decimals ?? 2);
  }

  const floored = Math.floor(amount * 100) / 100;
  return floored.toFixed(decimals ?? 2);
};

const formatValueWithKImpl = (args: FormatValueWithKArgs): string =>
  localizeDecimalSeparator(formatValueWithKRaw(args));

export const formatValueWithK = (amount: number, t: TFunction) => formatValueWithKImpl({ amount, mode: 'round', t });

export const formatValueWithKFloor = (amount: number, t: TFunction, decimals?: number) =>
  formatValueWithKImpl({ amount, mode: 'floor', t, decimals });

export const numberWithCommas = (number: string) => {
  const [integerPart, decimalPart] = number.toString().split('.');
  const german = isGermanNumberFormat();
  const grouped = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, german ? '.' : ',');
  return decimalPart === undefined ? grouped : `${grouped}${german ? ',' : '.'}${decimalPart}`;
};

export const toDecimals = (value: number) => {
  const valueToFixed = (Math.round(value * 100) / 100).toFixed(2);

  const valueWithCommas = numberWithCommas(valueToFixed);

  return valueWithCommas;
};

export const stripCurrencySymbol = (symbol?: string | null, fallback = '$') => {
  if (typeof symbol !== 'string' || symbol.length === 0) {
    return fallback;
  }
  return symbol.slice(-1);
};

export const getBirthDate = (date: string | Date) => {
  return new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
};

export const handlerExpiration = (valueArg: string) => {
  let newValue = valueArg;
  const ccExpirySeparator = '/';
  const ccExpiryPattern = /^\d{0,4}$/g;

  const unmask = (value: any) => value.replace(/[^\d]/g, '');

  const mask = (value: any, limit: any, separator: any) => {
    const output = [];
    for (let i = 0; i < value.length; i++) {
      if (i !== 0 && i % limit === 0) {
        output.push(separator);
      }

      if (i === 0 && value[i] > 1) {
        output.push('0');
        output.push(value[i]);
      } else if (i === 1 && value[i] > 2 && output[0] == 1) {
        output.push('2');
      } else {
        output.push(value[i]);
      }
    }

    return output.join('');
  };

  newValue = unmask(newValue);
  if (newValue.match(ccExpiryPattern)) {
    newValue = mask(newValue, 2, ccExpirySeparator);
    return newValue;
  }
};

export const handleCVC = (value: string) => {
  const ccCVCPattern = /^\d{0,3}$/g;

  if (value.match(ccCVCPattern)) return value;
};

export const handleCardNumber = (cardNumberValue: string) => {
  const ccNumberPattern = /^\d{0,16}$/g;
  const ccNumberSeparator = ' ';
  const unmask = (value: string) => value.replace(/[^\d]/g, '');
  const mask = (value: string, limit: number, separator: string) => {
    const output = [];
    for (let i = 0; i < value.length; i++) {
      if (i !== 0 && i % limit === 0) {
        output.push(separator);
      }

      output.push(value[i]);
    }

    return output.join('');
  };

  let newValue = unmask(cardNumberValue);
  if (newValue.match(ccNumberPattern)) {
    newValue = mask(newValue, 4, ccNumberSeparator);
    return newValue;
  }
};

export const blockEvent = <E extends SyntheticEvent>(e: E) => {
  e.preventDefault();
  e.stopPropagation();
};

export const generateGuid = () => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c: number) =>
    (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16),
  );
};

export const prefixImageUrl = (imageUrl?: string): string | undefined =>
  imageUrl?.indexOf('https://') === -1 ? `https://${imageUrl}` : imageUrl;

export const checkIfAppleDevice = () => navigator.userAgent.indexOf('Mac OS X') !== -1;

export const isIOSDevice = () => navigator.userAgent.match(/iPhone|iPad|iPod/i) !== null;

export const isAndroidDevice = () => navigator.userAgent.match(/android/i) !== null;

export const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const toTitleCase = (str?: string) => {
  if (!str) {
    return '';
  }
  return str
    .toLowerCase()
    .split(/[-_\s]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/** Same as CSS `text-transform: capitalize`: first letter of each word up, the rest untouched. */
export const capitalizeWords = (str?: string) => str?.replace(/(^|[\s-])\S/g, (match) => match.toUpperCase());
